import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, KeyRound } from "lucide-react";

export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin/leads" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      nav("/admin/leads", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Invalid username or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0F1C] text-white flex items-center justify-center px-4"
      data-testid="admin-login-page"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111827]/80 backdrop-blur p-8"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
          <Lock className="w-3.5 h-3.5" />
          Sahi Disha Admin
        </div>
        <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight">
          Sign in to the Leads dashboard
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Your credentials were shared with you separately.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor="username" className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Username
            </Label>
            <Input
              id="username"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-2 h-12 bg-white/5 border-white/10 text-white focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
              data-testid="admin-login-username"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Password
            </Label>
            <div className="mt-2 relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9 h-12 bg-white/5 border-white/10 text-white focus-visible:border-[#F97316] focus-visible:ring-[#F97316]/30"
                data-testid="admin-login-password"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-400" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium shadow-lg shadow-orange-500/20"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </div>
      </form>
    </div>
  );
}
